import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Header from "../../components/header";
import {
  BlueBox,
  GrayTruck,
  GreenBag,
  RedCalendar,
  YellowDollar,
} from "../../components/icons";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../components/ui-kit";
import { AppRoutes } from "../../constants/routes";
import Banner from "./components/Banner";
import CheckedItems from "./components/CheckedItems";
import DirectionCard from "./components/DirectionCard";
import Section from "./components/Section";
import Viewport from "./components/Viewport";
import ViewportMirrored from "./components/ViewportMirrored";
import bannerBg from "./images/bannerBg.png";
import backgroundImage from "./images/firstVpBg.png";
import vp4Img from "./images/integrations.png";
import vp5Img from "./images/inventory-management.png";
import vp3Img from "./images/orders.png";
import vp7Img from "./images/sales.png";
import vp6Img from "./images/shipping.png";
import vp2Img from "./images/subscription-options.png";

const Wrapper = styled.section`
  display: flex;
  height: 100vh;
  flex-direction: column;
`;

const Content = styled.div`
  height: calc(100vh - 56px);
  margin-top: 56px;
`;

const H1 = styled(Typography)`
  font-size: 42px;
  font-weight: 700;
  line-height: 54px;
  text-align: center;
`;

const Title = styled(Typography)`
  font-size: 33px;
  font-weight: 700;
  line-height: 44px;
`;

const GreenLabel = styled(Typography)`
  color: #00bc82;
  text-transform: uppercase;
  margin-bottom: 15px;
`;

const TitleDescription = styled(Typography)`
  text-align: center;
  color: #495b6c;
  margin-top: 20px;
`;

const Description = styled(Typography)`
  color: #495b6c;
  margin-top: 15px;
`;

const AccentTextGreen = styled.span`
  color: #2cd19e;
`;

const WrapperWithBg = styled.div`
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-color: linear-gradient(180deg, #f2f4f7 40%, #ffffff 30%);
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
`;

const LoginButton = styled(Button)`
  margin-right: 15px;
`;

const DirectionsCards = styled.div`
  display: flex;
  margin-top: 80px;
  gap: 20px;
`;

const StyledSection = styled(Section)`
  padding-bottom: 10rem;
`;

const directionCards = [
  {
    icon: <GreenBag />,
    label: "Order management",
    link: "https://www.fena.co/order-management",
  },
  {
    icon: <YellowDollar />,
    label: "Sales management",
    link: "https://www.fena.co/sales-activities",
  },
  {
    icon: <BlueBox />,
    label: "Inventory management",
    link: "https://www.fena.co/product-inventory",
  },
  {
    icon: <GrayTruck />,
    label: "Shipping and fulfilment",
    link: "",
  },
  {
    icon: <RedCalendar />,
    label: "Booking management",
    link: "https://www.fena.co/service-management",
  },
];

export const Landing = () => {
  const navigate = useNavigate();

  const goToSignIn = () => {
    navigate(AppRoutes.Open.Auth.SIGN_IN_START);
  };

  return (
    <Wrapper>
      <Header landing />
      <Content>
        <WrapperWithBg>
          <Section>
            <H1 variant={TypographyVariant.HEADER1}>
              Unleash the power of our all-in-one platform to manage your{" "}
              <AccentTextGreen>B2B</AccentTextGreen> and{" "}
              <AccentTextGreen>B2C</AccentTextGreen> sales
            </H1>
            <TitleDescription variant={TypographyVariant.HEADER4}>
              Plus, take advantage of Open Banking payments included in your
              subscription
            </TitleDescription>

            <Buttons>
              <LoginButton
                onClick={goToSignIn}
                variant={ButtonVariant.OUTLINED}
              >
                Login
              </LoginButton>
              {/* <Button onClick={goToSignUp} variant={ButtonVariant.CONTAINED}>
                Register
              </Button> */}
            </Buttons>

            <DirectionsCards>
              {directionCards.map((el) => {
                return <DirectionCard key={el.label} data={el} />;
              })}
            </DirectionsCards>
          </Section>
        </WrapperWithBg>

        <Viewport img={vp2Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            Easy Payments
          </GreenLabel>
          <Title>Payments included in your subscription</Title>
          <Description variant={TypographyVariant.HEADER4}>
            Fena Labs helps businesses with the design, creation, and
            maintenance of web and mobile applications. Our team of tech
            professionals will develop, test and refine your application using
            the latest technology and frameworks.
          </Description>
        </Viewport>

        <ViewportMirrored img={vp3Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            Various sales channels
          </GreenLabel>
          <Title>Order generation and aggregation</Title>
          <Description variant={TypographyVariant.HEADER4}>
            Generate, manage and aggregate orders from various sales channels
          </Description>
        </ViewportMirrored>

        <Viewport img={vp4Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            Sync your data across platforms
          </GreenLabel>
          <Title>Integrated with a wide range of platforms and software</Title>
          <CheckedItems text="Generate, manage and aggregate orders from various sales channels" />
          <CheckedItems text="Connect ecommerce platform, sales channels, accountancy platforms ad courier companies" />
          <CheckedItems text="Sync your data across platforms with the fena product inventory" />
        </Viewport>

        <ViewportMirrored img={vp5Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            All tools to store products
          </GreenLabel>
          <Title>Inventory and product management</Title>
          <CheckedItems text="Store your product details, track your inventory per product, connect your clients, customers and suppliers to your products" />
          <CheckedItems text="Easily search for products and customers to quickly create and retrieve orders" />
          <CheckedItems text="Add notes to products and orders for an in-depth view of sales activities" />
        </ViewportMirrored>

        <Viewport img={vp6Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            Keep your customers updated
          </GreenLabel>
          <Title>Shipping and fulfilment management</Title>
          <CheckedItems text="Generate and print shipping consignment slips and labels" />
          <CheckedItems text="Integrated with major courier companies" />
          <CheckedItems text="Keep your customers updated with the tracking number to follow their shipment status" />
        </Viewport>

        <ViewportMirrored img={vp7Img} withRightPadding imgWidth="50%">
          <GreenLabel variant={TypographyVariant.HEADER3}>
            Sales revenue at glance
          </GreenLabel>
          <Title>Sales activity management</Title>
          <CheckedItems text="Monitor your sales personnel performance. Track sales trends per sales person with top sellers across categories and products" />
          <CheckedItems text="Log discounts, shipping & margin components" />
          <CheckedItems text="Orders are linked to inventory level and decrease your product stock automatically" />
        </ViewportMirrored>

        <StyledSection>
          <Banner
            bgSrc={bannerBg}
            label="Request a demo We will:"
            buttonLabel="GET STARTED"
            buttonSrc={AppRoutes.Open.Auth.SIGN_IN_START}
            checkedItems={[
              { text: "Show you how to use our platform" },
              { text: "Address any of your questions Assist" },
              { text: "Assist with setting up your account" },
            ]}
          />
        </StyledSection>
      </Content>
    </Wrapper>
  );
};
